import {
  DriverShiftsService,
  ShiftsService,
  type PostPutShiftRequestAdmin,
  type PostShiftAcquireAdminRequest,
  type PostShiftTemplateActivationRequestAdmin,
  type PostStartGenerateShiftJobAdminRequest
} from '@/backend/market'
import { ShiftRepository } from '../../domain/repositories/ShiftRepository'
import { ShiftModel } from '../models/ShiftModel'
import type { ShiftFilterEntity } from '../../domain/entities/ShiftFilterEntity'
import { ShiftFilterModel } from '../models/ShiftFilterModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { TemplateActivationEntity } from '../../domain/entities/TemplateActivationEntity'
import { TemplateActivationModel } from '../models/TemplateActivationModel'
import type { CreateUpdateShiftEntity } from '../../domain/entities/CreateUpdateShift'
import { CreateUpdateShiftModel } from '../models/CreateUpdateShiftModel'
import type { AcquireShiftEntity } from '../../domain/entities/AcquireShiftEntity'
import { AcquireShiftModel } from '../models/AcquireShiftModel'
import type { StartJobEntity } from '../../domain/entities/StartJobEntity'
import { StartJobModel } from '../models/StartJobModel'
import type { DriverShiftFilterEntity } from '../../domain/entities/DriverShiftFilterEntity'
import { DriverShiftFilterModel } from '../models/DriverShiftFilterModel'
import { DriverShiftModel } from '../models/DriverShiftModel'
import type { DriverShiftEntity } from '../../domain/entities/DriverShiftEntity'

export class ShiftRepositoryImpl implements ShiftRepository {
  async getShifts(
    filter: ShiftFilterEntity
  ): Promise<PaginateEntity<ShiftModel[]> | { error: true }> {
    const shiftFilterModel = new ShiftFilterModel(filter)
    try {
      const response = await ShiftsService.getShiftsAdmin(shiftFilterModel)
      const shiftModels = response.items?.map((item) => new ShiftModel(item as ShiftModel))
      const result = new PaginateEntity<ShiftModel[]>(response, shiftModels)
      return result
    } catch {
      return { error: true }
    }
  }
  async getAllShifts(filter: ShiftFilterEntity): Promise<ShiftModel[] | { error: true }> {
    const shiftFilterModel = new ShiftFilterModel(filter)
    try {
      const response = await ShiftsService.getAllShiftsAdmin(shiftFilterModel)
      const shiftModels = response.shifts?.map((item) => new ShiftModel(item as ShiftModel))

      return shiftModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getDriverShifts(
    filter: DriverShiftFilterEntity
  ): Promise<PaginateEntity<DriverShiftEntity[]> | { error: true }> {
    const shiftFilterModel = new DriverShiftFilterModel(filter)
    try {
      const response = await DriverShiftsService.getDriverShiftsAdmin(shiftFilterModel)

      const shiftModels = response.items?.map((item) => new DriverShiftModel(item))
      const result = new PaginateEntity<ShiftModel[]>(response, shiftModels)
      return result
    } catch {
      return { error: true }
    }
  }
  async getDriverShift(id: string): Promise<DriverShiftEntity | { error: true }> {
    try {
      const response = await DriverShiftsService.getDriverShiftAdmin({
        id
      })

      const shiftModel = new DriverShiftModel(response)
      return shiftModel
    } catch {
      return { error: true }
    }
  }
  async getShift(id: string): Promise<ShiftModel | { error: true }> {
    try {
      const response = await ShiftsService.getShiftAdmin({ id })
      const shiftModel = new ShiftModel(response as ShiftModel)
      return shiftModel
    } catch {
      return { error: true }
    }
  }
  async templateActivation(
    data: TemplateActivationEntity
  ): Promise<{ success: true } | { error: true }> {
    const templateActivationModel = new TemplateActivationModel(data)
    try {
      await ShiftsService.postShiftAdmin({
        requestBody: templateActivationModel as PostShiftTemplateActivationRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async createShift(data: CreateUpdateShiftEntity): Promise<{ success: true } | { error: true }> {
    const createUpdateShiftModel = new CreateUpdateShiftModel(data)
    try {
      await ShiftsService.postShiftAdmin1({
        requestBody: createUpdateShiftModel as unknown as PostPutShiftRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async acquireShift(data: AcquireShiftEntity): Promise<{ success: true } | { error: true }> {
    const acquireShiftModel = new AcquireShiftModel(data)
    try {
      await ShiftsService.postAcquireShiftAdmin({
        requestBody: acquireShiftModel as PostShiftAcquireAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async releaseShift(
    driverShiftId: string,
    reason: string
  ): Promise<{ success: true } | { error: true }> {
    try {
      await ShiftsService.postReleaseShiftAdmin({
        requestBody: {
          driverShiftId,
          reason
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateShift(
    id: string,
    data: CreateUpdateShiftEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateShiftModel = new CreateUpdateShiftModel(data)
    try {
      await ShiftsService.putShiftAdmin({
        id,
        requestBody: createUpdateShiftModel as unknown as PostPutShiftRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async jobStart(data: StartJobEntity): Promise<{ success: true } | { error: true }> {
    const startJobModel = new StartJobModel(data)
    try {
      await ShiftsService.postShiftJobStartAdmin({
        requestBody: startJobModel as PostStartGenerateShiftJobAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteShift(id: string): Promise<{ success: true } | { error: true }> {
    try {
      await ShiftsService.deleteShiftsAdmin({
        requestBody: {
          shiftIds: [id]
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteAllShifts(): Promise<{ success: true } | { error: true }> {
    try {
      await ShiftsService.deleteAllShiftsAdmin()
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
